import { useState, useEffect } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface EmployeeFormProps {
  employee?: any | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function EmployeeForm({ employee, onClose, onSuccess }: EmployeeFormProps) {
  const [formData, setFormData] = useState({
    matricule: '',
    prenom: '',
    nom: '',
    date_naissance: '',
    cin: '',
    cnps: '',
    sexe: '' as 'M' | 'F' | '',
    situation_matrimoniale: '',
    adresse: '',
    telephone: '',
    email: '',
    poste: '',
    departement: '',
    salaire_base: '',
    salaire_brut_mensuel: '',
    date_embauche: '',
    date_depart: '',
    actif: true,
    cnps_inscrit: true,
    utilisateur_id: null as number | null,
  });

  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);

  // Pré-remplir le formulaire
  useEffect(() => {
    if (employee) {
      setFormData({
        matricule: employee.employeeId || '',
        prenom: employee.firstName || '',
        nom: employee.lastName || '',
        date_naissance: employee.birthDate ? employee.birthDate.split('T')[0] : '',
        cin: employee.cin || '',
        cnps: employee.socialSecurityNumber || '',
        sexe: employee.sexe || '',
        situation_matrimoniale: employee.situation_matrimoniale || '',
        adresse: employee.address || '',
        telephone: employee.phone || '',
        email: employee.email || '',
        poste: employee.position || '',
        departement: employee.department || '',
        salaire_base: employee.baseSalary?.toString() || '',
        salaire_brut_mensuel: employee.salaire_brut_mensuel?.toString() || '',
        date_embauche: employee.hireDate ? employee.hireDate.split('T')[0] : '',
        date_depart: employee.date_depart ? employee.date_depart.split('T')[0] : '',
        actif: employee.status === 'Actif',
        cnps_inscrit: employee.cnps_inscrit ?? true,
        utilisateur_id: employee.utilisateur_id || null,
      });
    }
  }, [employee]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Effacer erreur
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.matricule.trim()) newErrors.matricule = 'Le matricule est requis';
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
    if (!formData.prenom.trim()) newErrors.prenom = 'Le prénom est requis';
    if (!formData.date_naissance) newErrors.date_naissance = 'La date de naissance est requise';
    if (!formData.poste.trim()) newErrors.poste = 'Le poste est requis';
    if (!formData.date_embauche) newErrors.date_embauche = "La date d'embauche est requise";
    if (!formData.salaire_base || parseFloat(formData.salaire_base) <= 0) {
      newErrors.salaire_base = 'Le salaire de base doit être supérieur à 0';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        matricule: formData.matricule,
        nom: formData.nom,
        prenom: formData.prenom,
        date_naissance: formData.date_naissance || null,
        cin: formData.cin || null,
        cnps: formData.cnps || null,
        sexe: formData.sexe || null,
        situation_matrimoniale: formData.situation_matrimoniale || null,
        adresse: formData.adresse || null,
        telephone: formData.telephone || null,
        email: formData.email || null,
        poste: formData.poste,
        departement: formData.departement || null,
        salaire_base: parseFloat(formData.salaire_base),
        salaire_brut_mensuel: formData.salaire_brut_mensuel ? parseFloat(formData.salaire_brut_mensuel) : null,
        date_embauche: formData.date_embauche,
        date_depart: formData.date_depart || null,
        actif: formData.actif,
        cnps_inscrit: formData.cnps_inscrit,
        utilisateur_id: formData.utilisateur_id,
      };

      if (employee?.id) {
        await updateResource(`employes`,employee.id, payload);
        toast.success('Employé modifié avec succès !');
      } else {
        await createResource(`employes`, payload);
        toast.success('Employé créé avec succès !');
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      const serverErrors = error.response?.data?.errors;
      if (serverErrors) {
        const formatted: any = {};
        Object.keys(serverErrors).forEach(key => {
          formatted[key] = serverErrors[key][0];
        });
        setErrors(formatted);
        toast.error('Erreur de validation');
      } else {
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
      }
    } finally {
      setLoading(false);
    }
  };

  const departments = ['Direction', 'Ressources Humaines', 'Comptabilité', 'Commercial', 'Marketing', 'Production', 'Logistique', 'Informatique', 'Qualité', 'Maintenance'];
  const situations = ['Célibataire', 'Marié(e)', 'Divorcé(e)', 'Veuf(ve)'];

  return (
    <div className="bg-white rounded-lg shadow-sm border max-h-screen overflow-y-auto">
      <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {employee ? 'Modifier l\'employé' : 'Nouvel employé'}
          </h2>
          <button onClick={onClose} disabled={loading} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-8">
        {/* Informations personnelles */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Informations Personnelles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Matricule *</label>
              <input type="text" name="matricule" value={formData.matricule} onChange={handleChange} disabled={loading}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.matricule ? 'border-red-300' : 'border-gray-300'}`} />
              {(errors.matricule || errors.matricule) && <p className="mt-1 text-sm text-red-600">{errors.matricule}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
              <input type="text" name="prenom" value={formData.prenom} onChange={handleChange} disabled={loading}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.prenom ? 'border-red-300' : 'border-gray-300'}`} />
              {errors.prenom && <p className="mt-1 text-sm text-red-600">{errors.prenom}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
              <input type="text" name="nom" value={formData.nom} onChange={handleChange} disabled={loading}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.nom ? 'border-red-300' : 'border-gray-300'}`} />
              {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de naissance *</label>
              <input type="date" name="date_naissance" value={formData.date_naissance} onChange={handleChange} disabled={loading}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.date_naissance ? 'border-red-300' : 'border-gray-300'}`} />
              {errors.date_naissance && <p className="mt-1 text-sm text-red-600">{errors.date_naissance}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CIN</label>
              <input type="text" name="cin" value={formData.cin} onChange={handleChange} disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CNPS</label>
              <input type="text" name="cnps" value={formData.cnps} onChange={handleChange} disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sexe</label>
              <select name="sexe" value={formData.sexe} onChange={handleChange} disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Non spécifié</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Situation matrimoniale</label>
              <select name="situation_matrimoniale" value={formData.situation_matrimoniale} onChange={handleChange} disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Non spécifié</option>
                {situations.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Coordonnées */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Coordonnées</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
              <textarea name="adresse" value={formData.adresse} onChange={handleChange} rows={3} disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
              <input type="tel" name="telephone" value={formData.telephone} onChange={handleChange} disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={loading}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.email ? 'border-red-300' : 'border-gray-300'}`} />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>
          </div>
        </div>

        {/* Contractuelles */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Informations Contractuelles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
              <select name="departement" value={formData.departement} onChange={handleChange} disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Sélectionner...</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Poste *</label>
              <input type="text" name="poste" value={formData.poste} onChange={handleChange} disabled={loading}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.poste ? 'border-red-300' : 'border-gray-300'}`} />
              {errors.poste && <p className="mt-1 text-sm text-red-600">{errors.poste}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date d'embauche *</label>
              <input type="date" name="date_embauche" value={formData.date_embauche} onChange={handleChange} disabled={loading}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.date_embauche ? 'border-red-300' : 'border-gray-300'}`} />
              {errors.date_embauche && <p className="mt-1 text-sm text-red-600">{errors.date_embauche}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de départ</label>
              <input type="date" name="date_depart" value={formData.date_depart} onChange={handleChange} disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Salaire de base *</label>
              <input type="number" name="salaire_base" value={formData.salaire_base} onChange={handleChange} min="0" step="0.01" disabled={loading}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors.salaire_base ? 'border-red-300' : 'border-gray-300'}`} />
              {errors.salaire_base && <p className="mt-1 text-sm text-red-600">{errors.salaire_base}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Salaire brut mensuel</label>
              <input type="number" name="salaire_brut_mensuel" value={formData.salaire_brut_mensuel} onChange={handleChange} min="0" step="0.01" disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input type="checkbox" name="actif" checked={formData.actif} onChange={handleChange} disabled={loading} className="mr-2" />
                <span className="text-sm font-medium text-gray-700">Employé actif</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" name="cnps_inscrit" checked={formData.cnps_inscrit} onChange={handleChange} disabled={loading} className="mr-2" />
                <span className="text-sm font-medium text-gray-700">Inscrit CNPS</span>
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button type="button" onClick={onClose} disabled={loading}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50">
            Annuler
          </button>
          <button type="submit" disabled={loading}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 flex items-center space-x-2">
            {loading && <i className="ri-loader-4-line animate-spin"></i>}
            <span>{employee ? 'Modifier' : 'Créer'}</span>
          </button>
        </div>
      </form>
    </div>
  );
}